import React, { useState, useEffect } from "react";
import {
  Typography,
  Box,
  Button,
  IconButton,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Tooltip,
  Switch,
  FormControlLabel,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from "@mui/material";
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  PlayArrow as TestIcon,
  VpnKey as KeyIcon,
  DateRange as DateIcon,
} from "@mui/icons-material";
import amazonApi from "../../../../services/api/prepbusiness/amazon";

const AmazonCredentials = () => {
  const [loading, setLoading] = useState(false);
  const [credentials, setCredentials] = useState([]);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Dialogs
  const [openDialog, setOpenDialog] = useState(false);
  const [editingId, setEditingId] = useState(null);

  // Form State
  const [formData, setFormData] = useState({
    display_name: "",
    client_id: "",
    client_secret: "",
    refresh_token: "",
  });

  useEffect(() => {
    loadCredentials();
  }, []);

  const loadCredentials = async () => {
    setLoading(true);
    try {
      const response = await amazonApi.getAll();
      setCredentials(response.data || []);
      setError(null);
    } catch (err) {
      console.error("Failed to load Amazon credentials:", err);
      setError("Failed to load credentials.");
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (credential = null) => {
    if (credential) {
      setEditingId(credential.id);
      setFormData({
        display_name: credential.display_name || "",
        client_id: credential.client_id || "",
        client_secret: "", // Don't populate secret
        refresh_token: "", // Don't populate refresh token
      });
    } else {
      setEditingId(null);
      setFormData({
        display_name: "",
        client_id: "",
        client_secret: "",
        refresh_token: "",
      });
    }
    setOpenDialog(true);
    setError(null);
    setSuccess(null);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingId(null);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async () => {
    if (!formData.display_name || !formData.client_id) {
      setError("Please fill in Display Name and Client ID.");
      return;
    }
    // Secret and Refresh Token usually required for new
    if (!editingId && (!formData.client_secret || !formData.refresh_token)) {
      setError(
        "Client Secret and Refresh Token are required for new accounts.",
      );
      return;
    }

    setLoading(true);
    try {
      if (editingId) {
        // Only send updated fields
        const dataToSend = { ...formData };
        if (!dataToSend.client_secret) delete dataToSend.client_secret;
        if (!dataToSend.refresh_token) delete dataToSend.refresh_token;

        await amazonApi.update(editingId, dataToSend);
        setSuccess("Credentials updated successfully.");
      } else {
        await amazonApi.create(formData);
        setSuccess("Credentials added successfully.");
      }
      handleCloseDialog();
      loadCredentials();
    } catch (err) {
      console.error("Failed to save credentials:", err);
      setError(err.response?.data?.detail || "Failed to save credentials.");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this credential?")) {
      return;
    }
    setLoading(true);
    try {
      await amazonApi.delete(id);
      setSuccess("Credential deleted successfully.");
      loadCredentials();
    } catch (err) {
      console.error("Failed to delete credential:", err);
      setError("Failed to delete credential.");
    } finally {
      setLoading(false);
    }
  };

  const handleTest = async (id) => {
    setLoading(true);
    try {
      const response = await amazonApi.testSaved(id);
      if (response.data?.status === "success") {
        setSuccess("Connection successful!");
      } else {
        setError(
          `Connection failed: ${response.data?.message || "Unknown error"}`,
        );
      }
    } catch (err) {
      console.error("Test failed:", err);
      setError("Connection test failed.");
    } finally {
      setLoading(false);
    }
  };

  const handleTestForm = async () => {
    if (
      !formData.client_id ||
      !formData.client_secret ||
      !formData.refresh_token
    ) {
      setError(
        "Please fill in all credential fields (Client ID, Secret, Refresh Token) for testing.",
      );
      return;
    }

    setLoading(true);
    try {
      const response = await amazonApi.test(formData);
      if (response.data?.status === "success") {
        setSuccess("Connection successful! Credentials are valid.");
      } else {
        setError(
          `Connection failed: ${response.data?.message || "Unknown error"}`,
        );
      }
    } catch (err) {
      console.error("Form test failed:", err);
      setError(err.response?.data?.detail || "Connection test failed.");
    } finally {
      setLoading(false);
    }
  };

  const handleToggleActive = async (id, currentStatus) => {
    // Optimistic update or wait? Wait is safer.
    try {
      await amazonApi.toggleActive(id);
      loadCredentials(); // Reload to see updated status across all items (since it's exclusive)
    } catch (err) {
      console.error("Failed to toggle active status:", err);
      setError("Failed to update active status");
    }
  };

  return (
    <Box>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={3}
      >
        <Typography variant="h5">Amazon Credentials</Typography>
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
        >
          Add Account
        </Button>
      </Box>

      <Alert severity="info" sx={{ mb: 3 }}>
        <Typography variant="body2">
          <strong>How to Get Your Amazon SP-API Credentials:</strong>
        </Typography>
        <ol style={{ margin: 0, paddingLeft: "20px" }}>
          <li>Login to Amazon Solution Provider Portal</li>
          <li>Navigate to Settings Dashboard</li>
          <li>Click on "LWA credentials" View</li>
          <li>Copy Client identifier and Client Secret</li>
          <li>Go to Action → Edit App → Authorize</li>
          <li>Authorize App</li>
          <li>Copy Refresh Token</li>
        </ol>
        <Typography variant="body2" mt={1}>
          For more information, visit{" "}
          <a
            href="https://developer-docs.amazon.com/sp-api/docs/"
            target="_blank"
            rel="noopener noreferrer"
            style={{
              color: "#1976d2",
              textDecoration: "underline",
              fontWeight: "bold",
            }}
          >
            Amazon SP-API Documentation
          </a>
        </Typography>
      </Alert>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert
          severity="success"
          sx={{ mb: 2 }}
          onClose={() => setSuccess(null)}
        >
          {success}
        </Alert>
      )}

      {loading && (
        <CircularProgress sx={{ display: "block", margin: "20px auto" }} />
      )}

      {!loading && credentials.length === 0 ? (
        <Alert severity="info" sx={{ mt: 2 }}>
          No Amazon credentials found. Add one to get started.
        </Alert>
      ) : (
        <TableContainer component={Paper} elevation={1}>
          <Table sx={{ minWidth: 650 }} aria-label="amazon credentials table">
            <TableHead sx={{ bgcolor: "action.hover" }}>
              <TableRow>
                <TableCell>
                  <strong>Status</strong>
                </TableCell>
                <TableCell>
                  <strong>Display Name</strong>
                </TableCell>
                <TableCell>
                  <strong>Client ID</strong>
                </TableCell>
                <TableCell>
                  <strong>Created At</strong>
                </TableCell>
                <TableCell align="right">
                  <strong>Actions</strong>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {credentials.map((cred) => (
                <TableRow
                  key={cred.id}
                  hover
                  sx={{
                    bgcolor: cred.active_omni ? "action.selected" : "inherit",
                  }}
                >
                  <TableCell component="th" scope="row">
                    <FormControlLabel
                      control={
                        <Switch
                          checked={cred.active_omni || false}
                          onChange={() =>
                            handleToggleActive(cred.id, cred.active_omni)
                          }
                          name="active_omni"
                          color="primary"
                          size="small"
                        />
                      }
                      label={
                        cred.active_omni ? (
                          <Chip
                            label="Active"
                            color="primary"
                            size="small"
                            icon={<CheckCircleIcon />}
                          />
                        ) : (
                          <Typography variant="body2" color="textSecondary">
                            Inactive
                          </Typography>
                        )
                      }
                    />
                  </TableCell>
                  <TableCell>
                    <Box display="flex" alignItems="center" gap={1}>
                      <KeyIcon color="action" fontSize="small" />
                      <Typography variant="body1" fontWeight="medium">
                        {cred.display_name}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell sx={{ fontFamily: "monospace" }}>
                    {cred.client_id
                      ? `${cred.client_id.substring(0, 12)}...`
                      : "N/A"}
                  </TableCell>
                  <TableCell>
                    {cred.created_at && (
                      <Box display="flex" alignItems="center" gap={0.5}>
                        <DateIcon fontSize="small" color="action" />
                        {new Date(cred.created_at).toLocaleDateString()}
                      </Box>
                    )}
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title="Test Connection">
                      <IconButton
                        size="small"
                        onClick={() => handleTest(cred.id)}
                        color="info"
                      >
                        <TestIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Edit">
                      <IconButton
                        size="small"
                        onClick={() => handleOpenDialog(cred)}
                      >
                        <EditIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => handleDelete(cred.id)}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Create/Edit Dialog */}
      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {editingId ? "Edit Amazon Account" : "Add Amazon Account"}
        </DialogTitle>
        <DialogContent>
          <Box component="form" sx={{ mt: 1 }}>
            <TextField
              margin="normal"
              required
              fullWidth
              label="Display Name"
              name="display_name"
              value={formData.display_name}
              onChange={handleChange}
              helperText="A friendly name for this account"
            />
            <TextField
              margin="normal"
              required
              fullWidth
              label="LWA App ID (Client ID)"
              name="client_id"
              value={formData.client_id}
              onChange={handleChange}
            />
            <TextField
              margin="normal"
              required={!editingId}
              fullWidth
              label="LWA Client Secret"
              name="client_secret"
              type="password"
              value={formData.client_secret}
              onChange={handleChange}
              placeholder={editingId ? "Leave blank to keep unchanged" : ""}
              helperText={
                editingId
                  ? "Only enter if you want to update the secret"
                  : "Required for new accounts"
              }
            />
            <TextField
              margin="normal"
              required={!editingId}
              fullWidth
              label="Refresh Token"
              name="refresh_token"
              type="password"
              value={formData.refresh_token}
              onChange={handleChange}
              multiline
              rows={3}
              placeholder={editingId ? "Leave blank to keep unchanged" : ""}
              helperText={
                editingId
                  ? "Only enter if you want to update the token"
                  : "Required for new accounts"
              }
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleTestForm}
            color="info"
            disabled={
              loading ||
              !formData.client_id ||
              !formData.client_secret ||
              !formData.refresh_token
            }
          >
            Test Keys
          </Button>
          <Box sx={{ flex: "1 1 auto" }} />
          <Button onClick={handleCloseDialog} disabled={loading}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            color="primary"
            disabled={loading}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AmazonCredentials;
